import { useCallback, useEffect, useState } from 'react';
import { useNavigate, generatePath, useLocation } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  DocumentStatus,
  PermissionEnum,
  DocumentType,
  Document,
  BaseTransaction,
  Currency,
  formatBalance,
  formatDate,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useAddCommentToProblemTransactionDocumentMutation,
  useAddCommentToProblemTransactionMutation,
  useAddDocumentToProblemTransactionMutation,
  useChangeProblemTransactionDocumentCategoryMutation,
  useChangeProblemTransactionDocumentStatusMutation,
  useCreateProblemTransactionChatMutation,
  useDeleteCommentInProblemTransactionDocumentMutation,
  useDeleteCommentInProblemTransactionMutation,
  useDeleteProblemTransactionDocumentsMutation,
  useEditCommentInProblemTransactionDocumentMutation,
  useEditCommentInProblemTransactionMutation,
  useLazyGetCustomerInternalTransactionQuery,
  useLazyGetCustomerInternalTransactionWarningQuery,
  useLazyGetCustomerInternalTransactionWarningsQuery,
  useLazyGetCustomersAccountBalanceQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetProblemTransactionChatsQuery,
  useLazyGetProblemTransactionCommentsQuery,
  useLazyGetProblemTransactionDocumentByIdQuery,
  useLazyGetProblemTransactionDocumentFileQuery,
  useLazyGetProblemTransactionRecipientClientsOfficiasQuery,
  useLazyGetProblemTransactionsDocumentsQuery,
  useLazyGetProblemTransactionSenderClientsOfficiasQuery,
  useLazyGetTransactionAmlQuery,
  useLazyGetTransactionStatementsChangeLogsQuery,
  useUploadDocumentMutation,
} from '../../../../customers/store/services/customers';
import {
  changePanelState,
  setBlacklistData,
  setBlacklistDataItem,
  setBlacklistDataVisible,
  setBlacklistItemVisible,
  setPanelIsOpen,
} from '../../../store/slices/problem-transactions/internal-transaction';
import { Balance } from '../../../types';
import { Employee } from '../../../../../store/dictionaries/types';
import { UploadResponse } from '../../../../../store/documents/types';

const useProblemInternalTransaction = ({
  transactionId,
  activePermissions,
  documents,
  transactionDocument,
  backPath,
  customerId,
  transaction,
  currenciesList,
  currentEmployee,
  sendMessage,
}: {
  transactionId?: string;
  activePermissions?: PermissionEnum[];
  documents?: Document[];
  transactionDocument?: Document;
  backPath?: string;
  customerId?: string;
  transaction?: BaseTransaction;
  currenciesList?: Currency[];
  currentEmployee?: Employee;
  sendMessage?: SendMessage | null;
}) => {
  const [imgLink, setImgLink] = useState('');
  const [senderAccountBalance, setSenderAccountBalance] = useState<number>();
  const [recipientAccountBalance, setRecipientAccountBalance] =
    useState<number>();
  const [senderBalances, setSenderBalances] = useState<Balance[]>([]);
  const [recipientBalances, setRecipientBalances] = useState<Balance[]>([]);
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [getInternalAml, { isLoading: isLoadingAml }] =
    useLazyGetTransactionAmlQuery();
  const [getInternalLogs, { isLoading: isLoadingLog }] =
    useLazyGetCustomersTransactionLogsQuery();
  const [
    getCustomerInternalTransactionWarningsQuery,
    { isLoading: warningsIsLoading },
  ] = useLazyGetCustomerInternalTransactionWarningsQuery();
  const [getChats] = useLazyGetProblemTransactionChatsQuery();
  const [getSenderAccounts] =
    useLazyGetProblemTransactionSenderClientsOfficiasQuery();
  const [getRecipientAccounts] =
    useLazyGetProblemTransactionRecipientClientsOfficiasQuery();
  const [
    getCustomerInternalTransaction,
    { isLoading: transactionIsLoading, isFetching },
  ] = useLazyGetCustomerInternalTransactionQuery();
  const [getTransactionDocuments, { isFetching: documentsIsFetching }] =
    useLazyGetProblemTransactionsDocumentsQuery();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetProblemTransactionDocumentByIdQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetProblemTransactionDocumentFileQuery();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteProblemTransactionDocumentsMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeProblemTransactionDocumentCategoryMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeProblemTransactionDocumentStatusMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentToProblemTransactionDocumentMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInProblemTransactionDocumentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentInProblemTransactionDocumentMutation();
  const [
    getSenderAccountBalance,
    { isLoading: senderBalanceIsLoading, isFetching: senderBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [
    getRecipientAccountBalance,
    {
      isLoading: recipientBalanceIsLoading,
      isFetching: recipientBalanceIsFetching,
    },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateProblemTransactionChatMutation();
  const [
    getTransactionComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetProblemTransactionCommentsQuery();
  const [
    addCommentToTransaction,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToProblemTransactionMutation();
  const [
    editCommentInTransaction,
    {
      isLoading: editTransactionCommentInProgress,
      isSuccess: editTrancastionCommentIsSuccess,
    },
  ] = useEditCommentInProblemTransactionMutation();
  const [
    deleteCommentInTransaction,
    {
      isLoading: deleteCommentInTransactionInProgress,
      isSuccess: deleteCommentInTranscationIsSuccess,
    },
  ] = useDeleteCommentInProblemTransactionMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useUploadDocumentMutation();
  const [
    addDocumentsToTransaction,
    { isLoading: addDocumentInProgress, isSuccess: fileUploadIsSuccess },
  ] = useAddDocumentToProblemTransactionMutation();
  const [
    getInternalTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCustomerInternalTransactionWarningQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionStatementsChangeLogsQuery();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const getInternalTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    await getInternalTransactionWarningById({
      id,
      warningId,
    });
  };

  const createChatWithMessage = async ({
    message,
    userId,
    type,
  }: {
    message: string;
    userId?: string;
    type?: 'sender' | 'recipient';
  }) => {
    const title = `RFI: Internal transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    const body: {
      title: string;
      employeeId: string;
      organizationId: string;
      userId?: string;
    } = {
      title,
      employeeId: currentEmployee?.id as string,
      organizationId:
        type === 'recipient'
          ? transaction?.recipientAccount?.organizationId
          : transaction?.senderAccount?.organizationId,
    };

    if (userId) {
      body.userId = userId;
    }

    const response = (await createTransactionChat({
      id: transactionId as string,
      body,
    })) as { data: any };

    if (response && response.data && sendMessage) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: response?.data?.data?.id,
            content: message,
          },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
    }
  };

  const getTransationDocumentsAction = (cursor?: string) => {
    const body: {
      id: string;
      cursor?: string;
    } = {
      id: transactionId as string,
    };

    if (cursor) {
      body.cursor = cursor;
    }

    getTransactionDocuments(body);
  };

  const onUploadDocument = async (files: FileList, organizationId?: string) => {
    setUploadedFilesError([]);
    const nextUploadedFilesErrors: string[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const response = (await upload(files[file as any])) as unknown as {
          data?: UploadResponse[];
          error?: {
            originalStatus: number;
            data?: {
              message?: string;
            };
          };
          message?: string;
        };

        let nextFile;

        if (response && response.data) {
          nextFile = { ...response.data[0] };

          let fileName = fileItem?.name;

          if (
            response.data[0] &&
            response.data[0]?.mimeType?.includes('image')
          ) {
            fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
          }

          nextFile.name = fileName;
        } else if (response.error) {
          if (response.error.originalStatus === 413) {
            nextUploadedFilesErrors.push(
              `${files[file as any].name} size too large`,
            );
          } else {
            // eslint-disable-next-line no-lonely-if
            if (response?.error?.data?.message) {
              nextUploadedFilesErrors.push(response?.error?.data?.message);
            }
          }
        }

        return nextFile;
      }),
    );

    setUploadedFilesError(nextUploadedFilesErrors);

    if (filesResponse && filesResponse.length) {
      const uploadedFiles: { name: string; uploadId: string }[] = [];

      filesResponse.forEach((item) => {
        if (item && item.name && item.id) {
          uploadedFiles.push({
            name: item?.name,
            uploadId: item?.id,
          });
        }
      });

      if (uploadedFiles.length) {
        await Promise.all(
          uploadedFiles.map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              organizationId: string;
            } = {
              uploadId: item.uploadId,
              name: item.name,
              organizationId: organizationId as string,
            };

            const response = await addDocumentsToTransaction({
              id: transactionId as string,
              body,
            });

            return response;
          }),
        );
      }
    }
  };

  const onEditCommentInTransaction = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInTransaction({
      id: transactionId as string,
      url: `transactions-internal`,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInTransaction = async (commentId: string) => {
    await deleteCommentInTransaction({
      id: transactionId as string,
      url: `transactions-internal`,
      commentId,
    });
  };

  const getTransactionCommentsAction = async (cursor?: string) => {
    await getTransactionComments({
      id: transactionId as string,
      url: `transactions-internal`,
      cursor: cursor || '',
    });
  };

  const onAddCommentToTransaction = async (text: string) => {
    await addCommentToTransaction({
      id: transactionId as string,
      url: `transactions-internal`,
      body: {
        text,
      },
    });
  };

  const onCreateTransactionChat = (
    userIds?: string[],
    type?: 'recipient' | 'sender',
  ) => {
    const title = `RFI: Internal transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    if (!userIds?.length) {
      createTransactionChat({
        id: transactionId as string,
        body: {
          title,
          employeeId: currentEmployee?.id as string,
          organizationId:
            type === 'recipient'
              ? transaction?.recipientAccount?.organizationId
              : transaction?.senderAccount?.organizationId,
        },
      });
    } else {
      Promise.all(
        userIds.map((item) =>
          createTransactionChat({
            id: transactionId as string,
            body: {
              title,
              employeeId: currentEmployee?.id as string,
              organizationId:
                type === 'recipient'
                  ? transaction?.recipientAccount?.organizationId
                  : transaction?.senderAccount?.organizationId,
              userId: item,
            },
          }),
        ),
      );
    }
  };

  const getInternalLogsAction = useCallback(() => {
    getInternalLogs(transactionId as string);
  }, [transactionId]);

  const getAccountsBalanceAction = useCallback(async () => {
    const senderResponse = (await getSenderAccountBalance({
      id: transaction?.senderAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (senderResponse.data) {
      setSenderAccountBalance(senderResponse.data.data.edges[0].node.value);
      setSenderBalances(
        senderResponse.data.data.edges?.map((item) => item.node),
      );
    }

    const recipientResponse = (await getRecipientAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (recipientResponse.data) {
      setRecipientAccountBalance(
        recipientResponse.data.data.edges[0].node.value,
      );
      setRecipientBalances(
        recipientResponse.data.data.edges?.map((item) => item.node),
      );
    }
  }, [transaction]);

  const getInternalAmlAction = useCallback(() => {
    getInternalAml(transactionId as string);
  }, [transactionId]);

  const getInternalTransactionById = useCallback(() => {
    getCustomerInternalTransaction(transactionId as string);
    getInternalAml(transactionId as string);
    getCustomerInternalTransactionWarningsQuery(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_INTERNAL_LIST,
      )
    ) {
      getInternalLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }

    if (
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST)
    ) {
      getTransactionDocuments({ id: transactionId as string });
    }
  }, [transactionId, activePermissions]);

  const getTransactionDocumentyIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };

  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: transactionDocument?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  const handleClose = () => {
    if (backPath) {
      dispatch(setPanelIsOpen(false));

      setTimeout(() => {
        navigate(
          generatePath(`${backPath}${search || ''}`, {
            customerId,
          }),
        );
      }, 100);
    }
  };

  const handleCloseBlacklistData = () => {
    dispatch(setBlacklistDataVisible(false));
    dispatch(setBlacklistData(undefined));
    dispatch(setBlacklistItemVisible(false));
    dispatch(setBlacklistDataItem(undefined));
    dispatch(changePanelState({ state: true, transactionId }));
  };

  useEffect(() => {
    if (
      transaction &&
      transaction.senderAccount &&
      transaction.senderAccount?.organization?.type === 'CORPORATE'
    ) {
      getSenderAccounts(transaction.senderAccount.organizationId);
    }
  }, [transaction]);

  useEffect(() => {
    if (
      transaction &&
      transaction.recipientAccount &&
      transaction.recipientAccount?.organization?.type === 'CORPORATE'
    ) {
      getRecipientAccounts(transaction.recipientAccount.organizationId);
    }
  }, [transaction]);

  return {
    handleClose,
    transactionIsLoading,
    isLoadingAml,
    isLoadingLog,
    warningsIsLoading,
    isFetching,
    getTransactionDocumentyIdAction,
    downloadInProgress,
    downloadIsFetching,
    imgLink,
    onDeleteDocument,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onMoveDocument,
    documentCategoryChangeInProgress,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    commentInProgress,
    documentStatusChangeInProgress,
    onChangeCustomerDocumentsStatus,
    downloadFileIsSuccess,
    documentIsLoading,
    documentIsFetching,
    handleCloseBlacklistData,
    getInternalAmlAction,
    getAccountsBalanceAction,
    senderAccountBalance,
    recipientAccountBalance,
    senderBalances,
    recipientBalances,
    senderBalanceIsLoading,
    senderBalanceIsFetching,
    recipientBalanceIsLoading,
    recipientBalanceIsFetching,
    getInternalLogsAction,
    onCreateTransactionChat,
    chatCreateInProgress,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
    selectedDocumentId,
    setSelectedDocumentId,
    setUploadedFilesError,
    onUploadDocument,
    fileUploadInProgress,
    addDocumentInProgress,
    fileUploadIsSuccess,
    uploadedFilesError,
    documentsIsFetching,
    getTransationDocumentsAction,
    getSenderAccounts,
    getRecipientAccounts,
    createChatWithMessage,
    getInternalTransactionWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    getInternalTransactionById,
  };
};

export default useProblemInternalTransaction;
